import { useState } from "react";
import { Button, Dialog } from "@omnabase/ui";
import { purgeSummary } from "@omnabase/shared";
import { ApiError } from "../../lib/api";
import { purgeConversations, type PurgeAsk } from "../../lib/deleted";
import { useSession } from "../../shell/store";

/** The delete-for-good round (2026-10-28; brief docs/briefs/delete-for-good-round.md, its last section): the owner's one question before
 *  conversations in Deleted or Spam go for good, as Customers' bulk delete asks it: the count named, the count typed, no undo. "Empty …"
 *  asks for the whole bin (a bin over one batch goes call after call); "Delete now" for the ticked rows. When the bin changed before
 *  anything went, the dialog names the new count and asks again. */
const many = (n: number) => `${n} ${n === 1 ? "conversation" : "conversations"}`;

export function PurgeDialog({ slug, ask, onClose, onDone }: { slug: string; ask: PurgeAsk; onClose: () => void; /** after the removal: the list and the counts reload */ onDone: () => void }) {
  const { toast } = useSession();
  const [count, setCount] = useState(ask.count); const [changed, setChanged] = useState(false);
  const [typed, setTyped] = useState(""); const [busy, setBusy] = useState(false);
  const armed = typed.trim() === String(count);
  const submit = async () => {
    if (!armed || busy) return; setBusy(true);
    try {
      const r = await purgeConversations(slug, { ...ask, count });
      if (r.changedTo !== undefined) { setCount(r.changedTo); setChanged(true); setTyped(""); setBusy(false); if (r.changedTo === 0) onDone(); return; }
      toast(purgeSummary(r.removed, r.waiting), { kind: "success" }); onDone();
    } catch (e) { toast(e instanceof ApiError ? e.message : "Could not delete them", { kind: "error" }); setBusy(false); }
  };
  return (
    <Dialog title={`Delete ${many(count)} for good?`} onClose={onClose} intro="Their messages and attachments go now and cannot be restored. There is no undo."
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" disabled={!armed || busy} busy={busy} onClick={() => void submit()}>{`Delete ${many(count)}`}</Button></>}>
      {changed && <p className="ob-dialog__hint ob-dialog__hint--error" role="status">{`The view changed meanwhile: it holds ${many(count)} now.`}</p>}
      <div className="ob-dialog__field"><span className="ob-dialog__label">Type {count} to confirm</span><input className="ob-dialog__input" inputMode="numeric" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={String(count)} aria-label="Number of conversations" autoFocus /></div>
    </Dialog>
  );
}
